import * as THREE from 'three';
import toast from 'react-hot-toast';
import store from '../redux/store';
import { copyObject } from '../redux/Slice/globalStates';
import { handlePasteCopy } from './UtilsFunction';
import { findItem, pasteItem } from '../components/Copy-Paste';
import {
  getMultiSelection,
  clearMultiSelection,
} from './selectAllObjects';
import {
  selected_object,
  setSelectedMeshesForGrouping,
  update_assets_data,
} from '../redux/Slice/gridSlice';
import {
  attachMultiSelectionTransform,
  detachMultiSelectionTransform,
} from './multiSelectionTransform';
import { isLiveSceneObject3D } from './ThreeJsUtils/assetVisibilityUtils';
import { captureDiscreteHistoryBefore, recordDiscreteHistoryAfter } from './gridHistoryDiscrete';
import { suppressGridHistory } from './gridHistorySuppress';
import { duplicateImageAsset } from '../components/gridpagefunction/image';
import { duplicateMeshAsset } from '../components/gridpagefunction/Mesh';
import { duplicateVideoAsset } from '../components/gridpagefunction/Video';
import { duplicateAudioAsset } from '../components/gridpagefunction/Audio';
import { handleAddText } from '../components/gridpagefunction/Text';
import { duplicateHotspot } from '../components/gridpagefunction/HotSpot';
import { duplicateParticleAsset } from './particles/HandleDuplicatePartical';

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function snapshotSceneUuids(scene) {
  const set = new Set();
  scene?.traverse?.((obj) => {
    if (obj?.uuid) set.add(obj.uuid);
  });
  return set;
}

function findNewSceneObject(scene, beforeUuids, sourceUuid) {
  if (!scene) return null;

  let candidate = null;
  scene.traverse((obj) => {
    if (
      candidate ||
      !obj?.uuid ||
      beforeUuids.has(obj.uuid) ||
      obj.uuid === sourceUuid
    ) {
      return;
    }
    if (
      obj.parent === scene ||
      obj.deleteType ||
      obj.isLight ||
      obj.isCamera ||
      obj.objType
    ) {
      candidate = obj;
    }
  });

  if (candidate) return candidate;

  scene.traverse((obj) => {
    if (
      !candidate &&
      obj?.uuid &&
      !beforeUuids.has(obj.uuid) &&
      obj.uuid !== sourceUuid
    ) {
      candidate = obj;
    }
  });

  return candidate;
}

function resolveDuplicatedObject(scene, beforeUuids, sourceObject) {
  const selected = store.getState()?.assets_object?.select_object;
  if (
    isLiveSceneObject3D(selected) &&
    selected.uuid !== sourceObject?.uuid &&
    !beforeUuids.has(selected.uuid)
  ) {
    return selected;
  }
  return findNewSceneObject(scene, beforeUuids, sourceObject?.uuid);
}

function buildLightPasteItem(lightItem, sceneObject) {
  return {
    type: 'light',
    data: {
      ...lightItem,
      _id: undefined,
      id: undefined,
      position: sceneObject
        ? {
            x: sceneObject.position.x,
            y: sceneObject.position.y,
            z: sceneObject.position.z,
          }
        : lightItem.position,
      rotation: sceneObject
        ? {
            x: THREE.MathUtils.radToDeg(sceneObject.rotation.x),
            y: THREE.MathUtils.radToDeg(sceneObject.rotation.y),
            z: THREE.MathUtils.radToDeg(sceneObject.rotation.z),
          }
        : lightItem.rotation,
      scale: sceneObject
        ? {
            x: sceneObject.scale.x,
            y: sceneObject.scale.y,
            z: sceneObject.scale.z,
          }
        : lightItem.scale,
    },
  };
}

function buildCameraPasteItem(cameraItem, sceneObject, cameraProperties = []) {
  const baseName =
    cameraItem.name ||
    cameraItem.file_name ||
    cameraItem.temp_file_name ||
    sceneObject?.name ||
    'Camera';
  const existingCopies =
    cameraProperties.filter((cam) => {
      const name = cam.name || cam.file_name || cam.temp_file_name || '';
      return name.startsWith(`${baseName}_copy`);
    }) || [];

  return {
    type: 'camera',
    data: {
      type: sceneObject.type,
      name: `${baseName}_copy ${existingCopies.length + 1}`,
      fov: sceneObject.fov,
      aspect: sceneObject.aspect,
      near: sceneObject.near,
      far: sceneObject.far,
      left: sceneObject.left,
      right: sceneObject.right,
      top: sceneObject.top,
      bottom: sceneObject.bottom,
      zoom: sceneObject.zoom,
      position: sceneObject.position.toArray(),
      rotation: sceneObject.rotation.toArray(),
      scale: sceneObject.scale.toArray(),
    },
  };
}

function duplicateEmptyObject(emptyItem, sceneObject, objectt) {
  const scene = objectt?.scene;
  if (!scene) return null;

  const newId = `Empty_${Date.now()}`;
  const newEmpty = new THREE.Object3D();
  newEmpty.name = newId;

  const original =
    sceneObject ||
    scene.getObjectByName(
      emptyItem._id || emptyItem.id || emptyItem.url || emptyItem.name
    );

  if (original) {
    newEmpty.position.copy(original.position);
    newEmpty.rotation.copy(original.rotation);
    newEmpty.scale.copy(original.scale);
  }

  scene.add(newEmpty);

  const existingEmpties =
    store.getState()?.assets_object?.assets_data?.empty || [];
  const baseName =
    emptyItem.file_name ||
    emptyItem.temp_file_name ||
    emptyItem.name ||
    'Empty';
  const copies = existingEmpties.filter((entry) =>
    (entry.file_name || entry.name || '').startsWith(`${baseName}_copy`)
  );

  const newEmptyData = {
    ...emptyItem,
    _id: newId,
    id: newId,
    name: newId,
    file_name: `${baseName}_copy ${copies.length + 1}`,
    temp_file_name: `${baseName}_copy ${copies.length + 1}`,
  };

  store.dispatch(
    update_assets_data({
      type: 'empty',
      data: [...existingEmpties, newEmptyData],
    })
  );
  store.dispatch(selected_object(newEmpty));
  window.__selectObjectInScene?.(newId);
  objectt?.setSelectedObjectState?.(newEmpty);
  return newEmpty;
}

function findEmptyItemForSceneObject(sceneObject, assetsData = {}) {
  return (assetsData.empty || []).find(
    (entry) =>
      entry._id === sceneObject?.name ||
      entry.id === sceneObject?.name ||
      entry.url === sceneObject?.name ||
      entry.name === sceneObject?.name
  );
}

function createPasteItemHandler(objectt) {
  return pasteItem({
    dispatch: store.dispatch,
    scene: objectt?.scene,
    selectObjectInScene: (id) => window.__selectObjectInScene?.(id),
    setSelectedObjectState: objectt?.setSelectedObjectState,
    setAdditionalCameras: () => {},
    objectt,
  });
}

async function duplicateOneSceneObject(sceneObject, objectt, pasteItemFn) {
  const scene = objectt?.scene;
  if (!scene || !sceneObject) return null;

  const beforeUuids = snapshotSceneUuids(scene);
  const state = store.getState();
  const assetsObject = state.assets_object ?? {};
  const item =
    findItem(sceneObject, {
      light_data: assetsObject.light_data ?? [],
      assets_data: assetsObject.assets_data ?? {},
      particles: state.particles?.particles ?? [],
    }) ||
    (() => {
      const emptyItem = findEmptyItemForSceneObject(
        sceneObject,
        assetsObject.assets_data
      );
      return emptyItem ? { type: 'empty', data: emptyItem } : null;
    })();

  objectt?.transformControls?.detach?.();

  if (item?.type === 'image') {
    duplicateImageAsset(item.data, objectt, undefined, sceneObject);
  } else if (item?.type === 'mesh') {
    duplicateMeshAsset(item.data, objectt, undefined, sceneObject);
  } else if (item?.type === 'video') {
    duplicateVideoAsset(item.data, objectt, undefined, sceneObject);
  } else if (item?.type === 'audio') {
    duplicateAudioAsset(item.data, objectt, undefined, sceneObject);
  } else if (item?.type === 'text') {
    const duplicated = await handleAddText(
      { ...objectt, selectedObject: sceneObject },
      item.data,
      'duplicate'
    );
    if (duplicated) return duplicated;
  } else if (item?.type === 'hotspot') {
    const duplicated = duplicateHotspot(item.data, objectt);
    if (duplicated) return duplicated;
  } else if (item?.type === 'particle') {
    duplicateParticleAsset(item.data, objectt);
    await delay(150);
  } else if (item?.type === 'light') {
    pasteItemFn(buildLightPasteItem(item.data, sceneObject));
    await delay(150);
  } else if (item?.type === 'camera') {
    pasteItemFn(
      buildCameraPasteItem(
        item.data,
        sceneObject,
        assetsObject.camera_properties ?? []
      )
    );
  } else if (item?.type === 'empty') {
    return duplicateEmptyObject(item.data, sceneObject, objectt);
  } else {
    store.dispatch(copyObject(sceneObject));
    handlePasteCopy(objectt);
    await delay(50);
  }

  return resolveDuplicatedObject(scene, beforeUuids, sceneObject);
}

function getObjectsToDuplicate(objectt) {
  const multi = getMultiSelection();
  if (multi.length > 0) return [...multi];

  const selected = objectt?.selectedObject;
  if (isLiveSceneObject3D(selected)) return [selected];

  return [];
}

/**
 * Duplicate the current selection. When multiple objects are selected,
 * duplicates each one and re-selects all copies as a new multi-selection.
 */
export async function duplicateSelectedObjects(objectt) {
  const scene = objectt?.scene;
  const beforeSnapshot = scene ? captureDiscreteHistoryBefore(scene) : null;
  const endSuppress = suppressGridHistory();

  const finish = (result) => {
    endSuppress();
    if (beforeSnapshot && scene) {
      recordDiscreteHistoryAfter(beforeSnapshot, scene, 'duplicate');
    }
    return result;
  };

  const objects = getObjectsToDuplicate(objectt);

  if (objects.length === 0) {
    toast.error('Please select an object to duplicate.');
    return finish([]);
  }

  if (objects.length === 1) {
    store.dispatch(copyObject(objects[0]));
    handlePasteCopy(objectt);
    return finish([]);
  }

  detachMultiSelectionTransform();
  clearMultiSelection();

  const pasteItemFn = createPasteItemHandler(objectt);
  const duplicated = [];

  for (const sceneObject of objects) {
    const duplicate = await duplicateOneSceneObject(
      sceneObject,
      objectt,
      pasteItemFn
    );
    objectt?.transformControls?.detach?.();

    if (duplicate && isLiveSceneObject3D(duplicate)) {
      duplicated.push(duplicate);
    }
  }

  if (duplicated.length === 0) {
    toast.error('Failed to duplicate selected objects.');
    return finish([]);
  }

  if (duplicated.length === 1) {
    store.dispatch(selected_object(duplicated[0]));
    objectt?.transformControls?.attach?.(duplicated[0]);
    objectt?.setSelectedObjectState?.(duplicated[0]);
    objectt?.syncSelectAll?.(duplicated[0]);
    toast.success('Object duplicated.');
    return finish(duplicated);
  }

  store.dispatch(setSelectedMeshesForGrouping(duplicated));
  store.dispatch(selected_object(duplicated[0]));
  objectt?.setSelectedObjectState?.(duplicated[0]);
  objectt?.syncSelectAll?.(duplicated[0]);

  attachMultiSelectionTransform({
    scene: objectt?.scene,
    transformControls: objectt?.transformControls,
    objects: duplicated,
  });

  if (duplicated.length < objects.length) {
    toast.success(
      `Duplicated ${duplicated.length} of ${objects.length} objects. Some assets may still be loading.`
    );
  } else {
    toast.success(`Duplicated ${duplicated.length} objects.`);
  }
  return finish(duplicated);
}
